/**
 * ForgotPasswordPage — Password Reset (3-step self-service flow).
 *
 * Step 1: Enter School ID (if not in URL/env) + loginId → POST /auth/forgot-password
 * Step 2: Enter 6-digit OTP + new password → POST /auth/reset-password
 * Step 3: Success — navigate to /login
 *
 * Route: /forgot-password
 */

import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

import { AuthLayout } from '../../components/auth/AuthLayout';
import { AuthCard } from '../../components/auth/AuthCard';
import { AuthInput } from '../../components/auth/AuthInput';
import { AuthButton } from '../../components/auth/AuthButton';
import {
  MailIcon,
  LockIcon,
  ChevronLeftIcon,
  InfoIcon,
  EyeIcon,
  EyeOffIcon,
} from '../../components/auth/AuthIcons';

function useSchoolId(): string | null {
  const { search } = useLocation();
  return useMemo(() => {
    const fromUrl = new URLSearchParams(search).get('school');
    if (fromUrl && fromUrl.trim().length > 0) return fromUrl.trim();
    const fromEnv = import.meta.env.VITE_DEFAULT_SCHOOL_ID;
    return typeof fromEnv === 'string' && fromEnv.length > 0 ? fromEnv : null;
  }, [search]);
}

// Server's (English) message when present, else undefined so callers fall back to a
// translated client message (server messages are a Tier-1.5 follow-up).
function extractError(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || undefined;
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const schoolId = useSchoolId();

  const [manualSchoolId, setManualSchoolId] = useState('');
  const [loginId, setLoginId] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [otpLogId, setOtpLogId] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const effectiveSchoolId = schoolId ?? (manualSchoolId.trim() || null);

  const handleRequest = async () => {
    setError('');
    if (!effectiveSchoolId) {
      setError(t('common:forgot.err_school_required'));
      return;
    }
    if (
      schoolId === null &&
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(effectiveSchoolId)
    ) {
      setError(t('common:forgot.err_school_invalid'));
      return;
    }
    if (!loginId.trim()) {
      setError(t('common:forgot.err_login_required'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<{ data: { otpLogId: string; message: string } }>(
        '/auth/forgot-password',
        { schoolId: effectiveSchoolId, loginId: loginId.trim() },
      );
      setOtpLogId(res.data.data.otpLogId);
      setStep(2);
    } catch (err) {
      setError(extractError(err) ?? t('common:errors.something_wrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setError('');
    if (otp.trim().length !== 6) {
      setError(t('common:forgot.err_code'));
      return;
    }
    if (!newPassword) {
      setError(t('common:forgot.err_password'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { otpLogId, otp: otp.trim(), newPassword });
      setStep(3);
    } catch (err) {
      setError(extractError(err) ?? t('common:errors.something_wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard width={420}>
        {/* Back to login */}
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="flex items-center gap-1.5 bg-transparent border-none cursor-pointer text-[var(--t3)] text-[13px] font-semibold p-0 mb-6 hover:text-[var(--t2)]"
        >
          <ChevronLeftIcon />
          {t('common:forgot.back_to_signin')}
        </button>

        {step === 1 && (
          <>
            {/* Header */}
            <div className="mb-7">
              <div
                className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-4"
                style={{ background: 'var(--p-lt)', border: '1.5px solid var(--p-md)' }}
              >
                <LockIcon size={24} className="text-[var(--p)]" />
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-[var(--t1)] mb-1.5">
                {t('common:auth.forgot_password')}
              </h1>
              <p className="text-sm font-medium text-[var(--t3)] leading-relaxed">
                {t('common:forgot.subtitle')}
              </p>
            </div>

            <div className="flex flex-col gap-[18px]">
              {schoolId === null && (
                <div className="flex flex-col gap-1.5">
                  <AuthInput
                    label={t('common:forgot.school_id')}
                    type="text"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={manualSchoolId}
                    onChange={(e) => setManualSchoolId(e.target.value)}
                    icon={<InfoIcon />}
                    autoComplete="off"
                  />
                  <p className="text-[11px] font-medium text-[var(--t3)] m-0">
                    {t('common:forgot.school_id_hint')}
                  </p>
                </div>
              )}

              <AuthInput
                label={t('common:auth.login_id')}
                type="text"
                placeholder={t('common:auth.mobile_or_email')}
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                icon={<MailIcon />}
                hint={t('common:forgot.login_id_hint')}
                autoComplete="username"
              />

              {error && (
                <div
                  className="text-[13px] font-medium px-3 py-2 rounded-md"
                  style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA' }}
                  role="alert"
                >
                  {error}
                </div>
              )}

              <AuthButton
                type="button"
                variant="primary"
                fullWidth
                loading={loading}
                size="lg"
                onClick={handleRequest}
              >
                {t('common:forgot.send_code')}
              </AuthButton>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {/* Header */}
            <div className="mb-7">
              <div
                className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-4"
                style={{ background: 'var(--p-lt)', border: '1.5px solid var(--p-md)' }}
              >
                <LockIcon size={24} className="text-[var(--p)]" />
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-[var(--t1)] mb-1.5">
                {t('common:forgot.enter_code_title')}
              </h1>
              <p className="text-sm font-medium text-[var(--t3)] leading-relaxed">
                {t('common:forgot.code_sent_hint')}
              </p>
            </div>

            <div className="flex flex-col gap-[18px]">
              <AuthInput
                label={t('common:forgot.code_label')}
                type="text"
                inputMode="numeric"
                placeholder="123456"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                icon={<MailIcon />}
                autoComplete="one-time-code"
              />

              <AuthInput
                label={t('common:auth.new_password')}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('common:forgot.new_password_ph')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                icon={<LockIcon />}
                rightIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                onRightIconClick={() => setShowPassword((p) => !p)}
                hint={t('common:forgot.password_hint')}
                autoComplete="new-password"
              />

              {error && (
                <div
                  className="text-[13px] font-medium px-3 py-2 rounded-md"
                  style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA' }}
                  role="alert"
                >
                  {error}
                </div>
              )}

              <AuthButton
                type="button"
                variant="primary"
                fullWidth
                loading={loading}
                size="lg"
                onClick={handleReset}
              >
                {t('common:forgot.reset_password')}
              </AuthButton>

              <p className="text-center text-xs font-medium text-[var(--t4)]">
                {t('common:forgot.no_code')}{' '}
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setOtp('');
                    setOtpLogId('');
                    setError('');
                  }}
                  className="text-[var(--p)] font-bold bg-transparent border-none cursor-pointer hover:underline"
                >
                  {t('common:forgot.start_over')}
                </button>
              </p>
            </div>
          </>
        )}

        {step === 3 && (
          <div className="text-center py-2">
            {/* Green circle */}
            <div
              className="w-[72px] h-[72px] rounded-full mx-auto mb-5 flex items-center justify-center"
              style={{ background: 'var(--green-lt)', border: '1.5px solid #A7F3D0' }}
            >
              <MailIcon size={32} className="text-[var(--green)]" />
            </div>

            <h2 className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-[var(--t1)] mb-2.5">
              {t('common:forgot.success_title')}
            </h2>

            <p className="text-sm font-medium text-[var(--t3)] leading-[1.7] mb-6">
              {t('common:forgot.success_message')}
            </p>

            <AuthButton variant="primary" fullWidth onClick={() => navigate('/login')}>
              {t('common:auth.go_to_login')}
            </AuthButton>
          </div>
        )}
      </AuthCard>
    </AuthLayout>
  );
}

export default ForgotPasswordPage;
